/**
 * SyncIndicator
 *
 * Quiet, unobtrusive sync status row placed above the sidebar footer buttons.
 *
 * Visual states:
 *  - synced  — 6px dot in accent color, text "Synced"
 *  - offline — 6px dot in amber, text "Offline · N pending"
 *  - syncing — pulsing dot in accent color, text "Syncing..."
 *  - conflict — red dot, text "N conflict(s)"; click calls onResolveConflicts
 *
 * Reactivity:
 *  - useOnlineStatus() for online/offline transitions
 *  - Subscribes to sync state via SyncEventBus
 *  - Polls getPendingCount() every 5 s (cheap SQLite read, accurate count)
 *
 * Conflict resolution UI lives outside the dropdown menu (in UserMenu) so it
 * renders as a centered modal instead of a popover inside the settings menu.
 */

import { SyncState } from "@/types";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useOnlineStatus } from "../../lib/online";
import { useAuthStore } from "../../store/auth";
import { getSyncEventBus } from "../../lib/services";
import { getConflictCount, getPendingCount, getSyncState } from "../../lib/sync";
import { getSyncDisplayState, type DisplayState } from "../../lib/syncDisplay";

interface SyncIndicatorProps {
  /** Called when the user clicks the indicator while in a conflict state. */
  onResolveConflicts?: () => void;
  /** Called when the user clicks the indicator while in local mode. */
  onSignIn?: () => void;
}

export function SyncIndicator({ onResolveConflicts, onSignIn }: SyncIndicatorProps) {
  const { t } = useTranslation();
  const localMode = useAuthStore((s) => s.localMode && s.user === null);
  const isOnline = useOnlineStatus();
  const [pendingCount, setPendingCount] = useState(0);
  const [conflictCount, setConflictCount] = useState(0);
  const [syncState, setSyncState] = useState<SyncState>(getSyncState);

  // Poll pending + conflict count every 5 s
  useEffect(() => {
    let mounted = true;
    const check = () => {
      if (mounted) {
        setPendingCount(getPendingCount());
        setConflictCount(getConflictCount());
      }
    };
    check();
    const id = setInterval(check, 5000);
    return () => {
      mounted = false;
      clearInterval(id);
    };
  }, []);

  // React to sync engine state changes in real-time via SyncEventBus
  useEffect(() => {
    const unsubscribe = getSyncEventBus().subscribe("sync-state", (state) => {
      setSyncState(state);
      // Refresh counts immediately after a sync cycle
      if (state === "idle") {
        setPendingCount(getPendingCount());
        setConflictCount(getConflictCount());
      }
    });
    return unsubscribe;
  }, []);

  // Safety timeout: if stuck in "syncing" for more than 30 seconds,
  // reset to idle. This catches edge cases where the sync promise hangs
  // or broadcastSyncState("idle") is never reached.
  useEffect(() => {
    if (syncState !== "syncing") return;

    const timeout = setTimeout(() => {
      console.warn(
        "[Caliu] Sync state stuck on 'syncing' for 30s — resetting to idle",
      );
      setSyncState("idle");
    }, 30_000);

    return () => clearTimeout(timeout);
  }, [syncState]);

  // ── Determine display state ─────────────────────────────────────────────────

  const display = getSyncDisplayState({
    isOnline,
    syncState,
    pendingCount,
    conflictCount,
    localMode,
  });

  const dotClass: Record<DisplayState, string> = {
    // Neutral, not amber: local is a chosen state, not a pending problem.
    local: "bg-muted",
    synced: "bg-accent",
    pending: "bg-warning",
    offline: "bg-warning",
    syncing: "bg-accent",
    conflict: "bg-danger",
    error: "bg-danger",
  };

  const label: Record<DisplayState, string> = {
    local: t("syncIndicator.local"),
    synced: t("syncIndicator.synced"),
    pending: t("syncIndicator.pending", { count: pendingCount }),
    offline:
      pendingCount > 0
        ? t("syncIndicator.offlinePending", { count: pendingCount })
        : t("syncIndicator.offline"),
    syncing: t("syncIndicator.syncing"),
    conflict: t("syncIndicator.conflict", { count: conflictCount }),
    error: t("syncIndicator.error"),
  };

  const tooltip: Record<DisplayState, string> = {
    local: t("syncIndicator.localTooltip"),
    synced: t("syncIndicator.syncedTooltip"),
    pending: t("syncIndicator.pendingTooltip", { count: pendingCount }),
    offline:
      pendingCount > 0
        ? t("syncIndicator.offlinePendingTooltip", { count: pendingCount })
        : t("syncIndicator.offlineTooltip"),
    syncing:
      pendingCount > 0
        ? t("syncIndicator.syncingPendingTooltip", { count: pendingCount })
        : t("syncIndicator.syncingTooltip"),
    conflict: t("syncIndicator.conflictTooltip", { count: conflictCount }),
    error: t("syncIndicator.errorTooltip"),
  };

  return (
    <div
      title={tooltip[display]}
      className={`flex w-full items-center gap-[6px] h-7 px-1 select-none ${display === "conflict" || display === "local" ? "cursor-pointer" : "cursor-default"}`}
      onClick={
        display === "conflict"
          ? onResolveConflicts
          : display === "local"
            ? onSignIn
            : undefined
      }
    >
      {/* Status dot */}
      <span className="flex items-center justify-center w-[14px] shrink-0">
        <span
          className={`inline-block w-2 h-2 rounded-full ${dotClass[display]}`}
          style={{
            animation:
              display === "syncing"
                ? "caliu-sync-pulse 1.4s ease-in-out infinite"
                : "none",
            opacity: display === "synced" ? 0.7 : 1,
          }}
        />
      </span>
      {/* Label */}
      <span
        className={`text-xs ${
          display === "conflict" || display === "error"
            ? "text-danger"
            : display === "offline" || display === "pending"
              ? "text-warning"
              : "text-muted"
        }`}
      >
        {label[display]}
      </span>

      <style>{`
        @keyframes caliu-sync-pulse {
          0%, 100% { opacity: 0.4; transform: scale(0.85); }
          50%       { opacity: 1;   transform: scale(1.15); }
        }
      `}</style>
    </div>
  );
}
